import { MagicWandIcon } from '@carboncopyco/icons';
import { ColorHex, TextTooltip, Tooltip } from '@carboncopyco/ui';
import { getMaxZIndex } from '@carboncopyco/utils';
import type { Header } from '@tanstack/react-table';
import { useBoolean } from 'ahooks';
import classNames from 'classnames';
import { isObject } from 'lodash-es';
import type React from 'react';
import { useRef } from 'react';
import { EditWithAIDeprecated } from '../../../EditWithAI';

export type HeaderCellProps = {
  header: Header<Record<string, unknown>, unknown>;
  editableField: boolean;
  children: React.ReactNode;
  onBlur: (e: React.FocusEvent<HTMLTableHeaderCellElement>) => void;
};

export const HeaderCell = ({
  header,
  editableField,
  children,
  onBlur,
}: HeaderCellProps) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const anchorIconRef = useRef<HTMLSpanElement>(null);
  const [showEditWithAiDialog, setShowEditWithAiDialog] = useBoolean();
  const { table } = header.getContext();
  const { rows } = table.getCoreRowModel();
  const { accessorKey } = header.column.columnDef as any;
  const isEditWithAiSupprted =
    header.id !== 'rowActions' &&
    !isObject(rows[0]?.original[accessorKey]) &&
    !editableField;

  const columnValues = rows.flatMap((row) => {
    return row
      .getAllCells()
      .filter((cell) => cell.column.id === header.column.id)
      .map((cell) => cell.getValue());
  });

  return (
    <>
      <th
        className={classNames(
          'font-normal p-0 whitespace-nowrap group/th',
          editableField && 'focus:outline-ds-primary-300 cursor-pointer'
        )}
        contentEditable={editableField}
        suppressContentEditableWarning
        onBlur={onBlur}
      >
        <div
          ref={anchorRef}
          className={classNames(
            'min-h-[28px] h-full flex justify-between items-center py-1 px-2.5 border border-ds-natural-150 bg-ds-natural-100 group-first-of-type/th:rounded-tl-md group-last-of-type/th:rounded-tr-md',
            isEditWithAiSupprted && 'hover:bg-ds-natural-150'
          )}
        >
          {children}
          {isEditWithAiSupprted && (
            <>
              <span
                ref={anchorIconRef}
                className={classNames(
                  'group-hover/th:flex cursor-pointer',
                  !showEditWithAiDialog && 'hidden'
                )}
                onClick={setShowEditWithAiDialog.toggle}
              >
                <MagicWandIcon
                  size={16}
                  color={ColorHex.primary300}
                />
              </span>
              <Tooltip
                zIndex={getMaxZIndex() + 1}
                appendTo={() => document.body}
                delay={[700, 0]}
                placement="top"
                anchorRef={anchorIconRef}
                background="dark"
                shadow="lg"
              >
                <div className="px-1.5 pb-1 pt-0 leading-none break-words max-w-75 whitespace-normal">
                  <TextTooltip
                    label="Edit with AI"
                    color="text-ds-natural-50"
                  />
                </div>
              </Tooltip>
            </>
          )}
        </div>
      </th>
      <Tooltip
        visible={showEditWithAiDialog}
        interactive
        zIndex={getMaxZIndex() + 1}
        placement="bottom"
        appendTo={() => document.body}
        anchorRef={anchorRef}
        background="light"
        onClickOutside={setShowEditWithAiDialog.setFalse}
        shadow="none"
      >
        {showEditWithAiDialog && (
          <EditWithAIDeprecated
            onResponse={(res) => {
              table.options.meta?.updateColumn({
                accessorKey,
                values: res,
              });
            }}
            dataKey={String(header.column.columnDef.header)}
            dataToModify={columnValues}
            onClose={setShowEditWithAiDialog.setFalse}
          />
        )}
      </Tooltip>
    </>
  );
};
